import { ChangeDetectionStrategy, Component, OnInit, OnDestroy } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, switchMap, withLatestFrom, take, filter } from 'rxjs/operators';

import { NotificationTheme } from '@fcom/ui-components';
import { unsubscribe } from '@fcom/core/utils';
import { profile } from '@fcom/core/selectors';
import { AppState, CmsDataService } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { CmsArticle, CmsCollection, CMSelectionRules } from '@fcom/core-api';
import { Profile } from '@fcom/core-api/login';
import { finShare } from '@fcom/rx';
import { dismissedWithPoints } from '@fcom/loyalty-core/store/selectors';
import { MilestonesActions } from '@fcom/loyalty-core/store/actions';

@Component({
  selector: 'fin-milestones',
  templateUrl: 'milestones.component.html',
  styleUrls: ['./milestones.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MilestonesComponent implements OnInit, OnDestroy {
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  profile$: Observable<Profile>;
  milestoneItems$: Observable<CmsArticle[]>;
  tierPointsCurrentPeriod$: Observable<number>;
  newUnlockedMilestones$: Observable<string[]>;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<AppState>,
    private cmsDataService: CmsDataService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.profile$ = this.store$.pipe(profile(), finShare());

    this.subscriptions.add(
      this.profile$
        .pipe(
          take(1),
          map(({ currentPeriod }) => currentPeriod?.tierPointsCollected ?? 0),
          withLatestFrom(this.store$.pipe(dismissedWithPoints())),
          filter(([tierPoints, currentDismissedWithPoints]) => tierPoints < currentDismissedWithPoints)
        )
        .subscribe(() => {
          this.store$.dispatch(MilestonesActions.setDismissed({ points: 0 }));
        })
    );

    this.tierPointsCurrentPeriod$ = this.profile$.pipe(
      map((memberProfile) => memberProfile.currentPeriod?.tierPointsCollected ?? 0),
      finShare()
    );

    this.milestoneItems$ = this.profile$.pipe(
      withLatestFrom(this.languageService.translate('LOYALTY.fragments.milestonePoints.url')),
      switchMap(([{ tier, tierPointsAfterConversion }, fragmentUrl]) => {
        return this.cmsDataService
          .getFragmentJson<CMSelectionRules<CmsCollection<CmsArticle>>>(fragmentUrl, { tier })
          .pipe(
            map(
              (response) =>
                response?.items?.[0]?.items?.filter(
                  (cmsArticle) => cmsArticle.localSettings?.milestonePoints > (tierPointsAfterConversion || 0)
                ) || []
            )
          );
      }),
      finShare()
    );

    this.newUnlockedMilestones$ = combineLatest([
      this.tierPointsCurrentPeriod$,
      this.milestoneItems$,
      this.store$.pipe(dismissedWithPoints()),
    ]).pipe(
      map(([currentPoints, milestoneItems, currentDismissedWithPoints]) => {
        const notDismissedMilestoneItems = milestoneItems.filter(
          (item) => item.localSettings?.milestonePoints > currentDismissedWithPoints
        );
        return notDismissedMilestoneItems
          .filter((item) => item.localSettings?.milestonePoints <= currentPoints)
          .map((item) => item.related[0].detailText);
      }),
      distinctUntilChanged()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  trackByFn(_index: number, item: CmsArticle): string {
    return item.contentId.toString();
  }

  dismissMilestones(points: number): void {
    this.store$.dispatch(MilestonesActions.setDismissed({ points }));
  }
}
